import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { BarChart3, TrendingUp, Users, Eye, Star, Zap, MonitorPlay } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface MonthlyViewData {
  month: string;
  views: number;
}

interface WorkViewData {
  title: string;
  view_count: number;
}

interface WorkItem {
  id: string;
  title: string;
  view_count: number;
}

interface WorkMetrics {
  views: number;
  sessions: number;
  ratings: { like: number; dislike: number; love: number };
  slices: number;
}

const viewsChartConfig = {
  views: {
    label: 'Watch Sessions',
    color: 'hsl(var(--primary))',
  },
} satisfies ChartConfig;

const contentChartConfig = {
  view_count: {
    label: 'Views',
    color: 'hsl(var(--primary))',
  },
} satisfies ChartConfig;

const getStartDate = (range: string): Date | null => {
  const now = new Date();
  if (range === '7d') return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  if (range === '30d') return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  if (range === '90d') return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
  return null; // all-time
};

const Analytics = () => {
  const { publisher } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('90d');
  const [selectedWorkId, setSelectedWorkId] = useState<string | null>(null);

  const [works, setWorks] = useState<WorkItem[]>([]);
  const [totalViews, setTotalViews] = useState(0);
  const [totalSessions, setTotalSessions] = useState(0);
  const [totalRatings, setTotalRatings] = useState(0);
  const [monthlyViews, setMonthlyViews] = useState<MonthlyViewData[]>([]);
  const [contentBreakdown, setContentBreakdown] = useState<WorkViewData[]>([]);
  const [selectedWorkMetrics, setSelectedWorkMetrics] = useState<WorkMetrics | null>(null);
  const [loadingWorkMetrics, setLoadingWorkMetrics] = useState(false);

  useEffect(() => {
    if (publisher?.id) {
      fetchAnalyticsData(publisher.id, timeRange);
    }
  }, [publisher, timeRange]);

  useEffect(() => {
    if (selectedWorkId && publisher?.id) {
      fetchWorkMetrics(selectedWorkId, timeRange);
    } else {
      setSelectedWorkMetrics(null);
    }
  }, [selectedWorkId, timeRange]);

  const fetchAnalyticsData = async (publisherId: string, range: string) => {
    setLoading(true);
    try {
      const { data: worksData, error: worksError } = await supabase
        .from('works')
        .select('id, title, view_count')
        .eq('publisher_id', publisherId)
        .eq('status', 'published');

      if (worksError) {
        console.error('Error fetching works:', worksError);
        return;
      }

      if (!worksData || worksData.length === 0) {
        setWorks([]);
        setLoading(false);
        return;
      }

      const items: WorkItem[] = worksData.map(w => ({
        id: w.id,
        title: w.title,
        view_count: w.view_count ?? 0,
      }));
      setWorks(items);
      const workIds = items.map((w: WorkItem) => w.id);

      // Content breakdown sorted by view count (always all-time since view_count is cumulative)
      const sorted = [...items].sort(
        (a: WorkItem, b: WorkItem) => (b.view_count || 0) - (a.view_count || 0)
      );
      setContentBreakdown(
        sorted.map((w: WorkItem) => ({ title: w.title, view_count: w.view_count || 0 }))
      );
      setTotalViews(
        items.reduce((sum: number, w: WorkItem) => sum + (w.view_count || 0), 0)
      );

      const startDate = getStartDate(range);

      // Watch sessions with date filter
      let watchQuery = supabase
        .from('user_watch_history')
        .select('last_watched_at')
        .in('work_id', workIds);

      if (startDate) {
        watchQuery = watchQuery.gte('last_watched_at', startDate.toISOString());
      }

      const { data: watchHistory } = await watchQuery;

      // Build month buckets based on time range
      const monthCount = range === '7d' ? 1 : range === '30d' ? 2 : range === '90d' ? 3 : 6;
      const monthBuckets: { key: string; month: string; views: number }[] = [];
      for (let i = monthCount - 1; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        monthBuckets.push({
          key: `${d.getFullYear()}-${d.getMonth()}`,
          month: d.toLocaleString('default', { month: 'short' }),
          views: 0,
        });
      }

      (watchHistory || []).forEach((row: { last_watched_at: string | null }) => {
        if (!row.last_watched_at) return;
        const d = new Date(row.last_watched_at);
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        const entry = monthBuckets.find(m => m.key === key);
        if (entry) entry.views++;
      });

      setMonthlyViews(monthBuckets.map(({ month, views }) => ({ month, views })));
      setTotalSessions((watchHistory || []).length);

      // Ratings with date filter
      let ratingsQuery = supabase
        .from('user_ratings')
        .select('id')
        .in('content_id', workIds);

      if (startDate) {
        ratingsQuery = ratingsQuery.gte('rated_at', startDate.toISOString());
      }

      const { data: ratings } = await ratingsQuery;
      setTotalRatings((ratings || []).length);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkMetrics = async (workId: string, range: string) => {
    setLoadingWorkMetrics(true);
    try {
      const startDate = getStartDate(range);

      // Fetch all metrics in parallel
      const [workRes, sessionsRes, ratingsRes, slicesRes] = await Promise.all([
        supabase.from('works').select('view_count').eq('id', workId).single(),
        (() => {
          let q = supabase.from('user_watch_history').select('id').eq('work_id', workId);
          if (startDate) q = q.gte('last_watched_at', startDate.toISOString());
          return q;
        })(),
        (() => {
          let q = supabase.from('user_ratings').select('rating_type').eq('content_id', workId);
          if (startDate) q = q.gte('rated_at', startDate.toISOString());
          return q;
        })(),
        (() => {
          let q = supabase.from('slice_transactions').select('amount').eq('work_id', workId);
          if (startDate) q = q.gte('created_at', startDate.toISOString());
          return q;
        })(),
      ]);

      const ratingBreakdown = { like: 0, dislike: 0, love: 0 };
      (ratingsRes.data || []).forEach((r: any) => {
        if (r.rating_type in ratingBreakdown) {
          ratingBreakdown[r.rating_type as keyof typeof ratingBreakdown]++;
        }
      });

      setSelectedWorkMetrics({
        views: workRes.data?.view_count || 0,
        sessions: (sessionsRes.data || []).length,
        ratings: ratingBreakdown,
        slices: (slicesRes.data || []).reduce(
          (sum: number, s: any) => sum + Math.abs(s.amount || 0),
          0
        ),
      });
    } catch (error) {
      console.error('Error fetching work metrics:', error);
    } finally {
      setLoadingWorkMetrics(false);
    }
  };

  if (loading) {
    return (
      <div className='p-4 md:p-6 space-y-4 md:space-y-6'>
        <div>
          <h1 className='text-2xl md:text-3xl font-bold'>Analytics</h1>
          <p className='text-muted-foreground text-sm md:text-base'>
            Track your content performance and audience insights
          </p>
        </div>
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6'>
          {[1, 2, 3, 4].map(i => (
            <Card key={i}>
              <CardHeader><Skeleton className='h-4 w-28' /></CardHeader>
              <CardContent><Skeleton className='h-8 w-20' /></CardContent>
            </Card>
          ))}
        </div>
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6'>
          <Card><CardContent className='pt-6'><Skeleton className='h-72 w-full' /></CardContent></Card>
          <Card><CardContent className='pt-6'><Skeleton className='h-72 w-full' /></CardContent></Card>
        </div>
      </div>
    );
  }

  const hasWorks = contentBreakdown.length > 0;
  const hasViewsByWork = contentBreakdown.some(w => w.view_count > 0);
  const hasSessionData = monthlyViews.some(m => m.views > 0);

  const timeRangeLabel = timeRange === '7d' ? 'Last 7 days'
    : timeRange === '30d' ? 'Last 30 days'
    : timeRange === '90d' ? 'Last 90 days'
    : 'All time';

  return (
    <div className='p-4 md:p-6 space-y-4 md:space-y-6'>
      {/* Header with filters */}
      <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4'>
        <div>
          <h1 className='text-2xl md:text-3xl font-bold'>Analytics</h1>
          <p className='text-muted-foreground text-sm md:text-base'>
            Track your content performance and audience insights
          </p>
        </div>
        <div className='flex gap-3 flex-wrap'>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={selectedWorkId || 'all'}
            onValueChange={(v) => setSelectedWorkId(v === 'all' ? null : v)}
          >
            <SelectTrigger className="w-60">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All works (overview)</SelectItem>
              {works.map(w => (
                <SelectItem key={w.id} value={w.id}>{w.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Per-Title Drill-Down */}
      {selectedWorkId && (
        <>
          <Separator />
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <MonitorPlay className='h-5 w-5' />
                {works.find(w => w.id === selectedWorkId)?.title || 'Selected Work'}
              </CardTitle>
              <CardDescription>Individual metrics for {timeRangeLabel.toLowerCase()}</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingWorkMetrics ? (
                <div className='grid grid-cols-2 lg:grid-cols-4 gap-4'>
                  {[1, 2, 3, 4].map(i => (
                    <Skeleton key={i} className='h-20 w-full rounded-lg' />
                  ))}
                </div>
              ) : selectedWorkMetrics ? (
                <div className='grid grid-cols-2 lg:grid-cols-4 gap-4'>
                  <div className='p-4 rounded-lg bg-muted/20'>
                    <div className='flex items-center gap-2 mb-2'>
                      <Eye className='h-4 w-4 text-muted-foreground' />
                      <p className='text-xs text-muted-foreground'>Views (all-time)</p>
                    </div>
                    <p className='text-xl font-bold'>{selectedWorkMetrics.views.toLocaleString()}</p>
                  </div>
                  <div className='p-4 rounded-lg bg-muted/20'>
                    <div className='flex items-center gap-2 mb-2'>
                      <TrendingUp className='h-4 w-4 text-muted-foreground' />
                      <p className='text-xs text-muted-foreground'>Watch Sessions</p>
                    </div>
                    <p className='text-xl font-bold'>{selectedWorkMetrics.sessions.toLocaleString()}</p>
                  </div>
                  <div className='p-4 rounded-lg bg-muted/20'>
                    <div className='flex items-center gap-2 mb-2'>
                      <Star className='h-4 w-4 text-muted-foreground' />
                      <p className='text-xs text-muted-foreground'>Ratings</p>
                    </div>
                    <p className='text-xl font-bold'>
                      {selectedWorkMetrics.ratings.love + selectedWorkMetrics.ratings.like + selectedWorkMetrics.ratings.dislike}
                    </p>
                    <p className='text-xs text-muted-foreground mt-1'>
                      {selectedWorkMetrics.ratings.love} love, {selectedWorkMetrics.ratings.like} like, {selectedWorkMetrics.ratings.dislike} dislike
                    </p>
                  </div>
                  <div className='p-4 rounded-lg bg-muted/20'>
                    <div className='flex items-center gap-2 mb-2'>
                      <Zap className='h-4 w-4 text-muted-foreground' />
                      <p className='text-xs text-muted-foreground'>Slices Received</p>
                    </div>
                    <p className='text-xl font-bold'>{selectedWorkMetrics.slices.toLocaleString()}</p>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </>
      )}

      {/* Stats Overview */}
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6'>
        <Card>
          <CardHeader>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Total Views</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='flex items-center justify-between'>
              <div>
                <div className='text-2xl font-bold'>{totalViews.toLocaleString()}</div>
                <p className='text-xs text-muted-foreground mt-1'>Across all works (all-time)</p>
              </div>
              <Eye className='h-8 w-8 text-muted-foreground' />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Published Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='flex items-center justify-between'>
              <div>
                <div className='text-2xl font-bold'>{works.length}</div>
                <p className='text-xs text-muted-foreground mt-1'>Active titles</p>
              </div>
              <Users className='h-8 w-8 text-muted-foreground' />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Watch Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='flex items-center justify-between'>
              <div>
                <div className='text-2xl font-bold'>{totalSessions.toLocaleString()}</div>
                <p className='text-xs text-muted-foreground mt-1'>{timeRangeLabel}</p>
              </div>
              <TrendingUp className='h-8 w-8 text-muted-foreground' />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Ratings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='flex items-center justify-between'>
              <div>
                <div className='text-2xl font-bold'>{totalRatings.toLocaleString()}</div>
                <p className='text-xs text-muted-foreground mt-1'>{timeRangeLabel}</p>
              </div>
              <Star className='h-8 w-8 text-muted-foreground' />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6'>
        {/* Watch Sessions Over Time */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <BarChart3 className='h-5 w-5' />
              Watch Sessions Over Time
            </CardTitle>
            <CardDescription>
              Monthly watch sessions ({timeRangeLabel.toLowerCase()})
            </CardDescription>
          </CardHeader>
          <CardContent>
            {hasSessionData ? (
              <ChartContainer config={viewsChartConfig} className='min-h-[300px] w-full'>
                <BarChart accessibilityLayer data={monthlyViews}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey='month'
                    tickLine={false}
                    tickMargin={10}
                    axisLine={false}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <ChartLegend content={<ChartLegendContent />} />
                  <Bar dataKey='views' fill='var(--color-views)' radius={4} />
                </BarChart>
              </ChartContainer>
            ) : (
              <div className='flex flex-col items-center justify-center min-h-[300px] text-muted-foreground'>
                <BarChart3 className='h-12 w-12 mb-3 opacity-40' />
                <p className='font-medium'>No watch sessions yet</p>
                <p className='text-sm mt-1 text-center'>
                  Watch data will appear here as viewers watch your content
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Views by Title */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <TrendingUp className='h-5 w-5' />
              Views by Title
            </CardTitle>
            <CardDescription>
              Total views per published work (all-time)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {hasWorks && hasViewsByWork ? (
              <ChartContainer config={contentChartConfig} className='min-h-[300px] w-full'>
                <BarChart accessibilityLayer data={contentBreakdown} layout='vertical'>
                  <CartesianGrid horizontal={false} />
                  <XAxis type='number' tickLine={false} axisLine={false} />
                  <YAxis
                    dataKey='title'
                    type='category'
                    tickLine={false}
                    axisLine={false}
                    width={120}
                    tick={{ fontSize: 12 }}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey='view_count' fill='var(--color-view_count)' radius={4} />
                </BarChart>
              </ChartContainer>
            ) : (
              <div className='flex flex-col items-center justify-center min-h-[300px] text-muted-foreground'>
                <TrendingUp className='h-12 w-12 mb-3 opacity-40' />
                <p className='font-medium'>
                  {hasWorks ? 'No views recorded yet' : 'No published works yet'}
                </p>
                <p className='text-sm mt-1 text-center'>
                  {hasWorks
                    ? 'Views will accumulate as viewers discover your content'
                    : 'Publish your first work to start tracking views'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Mux Analytics Placeholder */}
      <Card className='border-primary/20 bg-primary/5'>
        <CardHeader>
          <div className='flex items-start gap-3'>
            <MonitorPlay className='h-5 w-5 text-primary mt-0.5 flex-shrink-0' />
            <div>
              <CardTitle className='text-sm font-medium'>Mux Video Analytics Coming Soon</CardTitle>
              <CardDescription className='text-xs mt-1'>
                Advanced streaming metrics including viewer watch time, play count, rebuffer rate,
                quality of experience, and time-to-first-frame will be available once Mux Data
                integration is complete.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
    </div>
  );
};

export default Analytics;
